import { ArrowLeft, ArrowRight, Check, Clock, FlaskConical, ListChecks } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LAB_BY_ID } from "../data/labs";
import { lessonsFor, localizeLab, stagesFor } from "../data/localized";
import { useT } from "../i18n";
import { QUESTION_BY_ID } from "../data/questions";
import { QuestionView } from "../components/QuestionView";
import { RichText } from "../components/RichText";
import { useDocumentTitle } from "../components/ui";
import { completeLesson, recordAnswer } from "../lib/progress";
import { isCorrect } from "../lib/scoring";
import { stableOptionOrder } from "../lib/session";
import { progressStore, useProgress } from "../lib/store";
import { NotFoundPage } from "./NotFoundPage";
import { RequireAuth } from "../components/Auth";

function QuickCheck({ ids }: { ids: string[] }) {
  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const t = useT();
  const questions = ids.map((id) => QUESTION_BY_ID.get(id)).filter((q) => q !== undefined);
  const correct = questions.filter((q) => revealed[q.id] && isCorrect(answers[q.id] ?? [], q.answer)).length;
  const doneCount = Object.keys(revealed).length;

  return (
    <div className="stack">
      {questions.map((q, i) => (
        <div key={q.id} className="card">
          <QuestionView
            question={q}
            order={stableOptionOrder(q)}
            selected={answers[q.id] ?? []}
            onChange={(next) => setAnswers((a) => ({ ...a, [q.id]: next }))}
            revealed={!!revealed[q.id]}
            number={i + 1}
          />
          {!revealed[q.id] && (
            <button
              type="button"
              className="btn primary mt"
              disabled={!answers[q.id]?.length}
              onClick={() => {
                progressStore.update((p) => recordAnswer(p, q.id, isCorrect(answers[q.id] ?? [], q.answer), Date.now()));
                setRevealed((r) => ({ ...r, [q.id]: true }));
              }}
            >
              {t("Kiểm tra", "Check")}
            </button>
          )}
        </div>
      ))}
      {doneCount === questions.length && questions.length > 0 && (
        <div className={`callout ${correct === questions.length ? "ok" : "warn"}`}>
          {t(`Bạn đúng ${correct}/${questions.length}.`, `You got ${correct}/${questions.length}.`)}{" "}
          {correct === questions.length
            ? t("Xuất sắc — sẵn sàng sang bài tiếp!", "Excellent — ready for the next lesson!")
            : t("Đọc lại phần giải thích, các câu sai sẽ xuất hiện trong mục Ôn tập.", "Re-read the explanations; missed questions will show up in Review.")}
        </div>
      )}
    </div>
  );
}

export function LessonPage() {
  const { lessonId } = useParams();
  const p = useProgress();
  const t = useT();
  const LESSONS = lessonsFor(p.lang);
  const STAGES = stagesFor(p.lang);
  const lesson = LESSONS.find((l) => l.id === lessonId);
  useDocumentTitle(lesson?.title ?? t("Không tìm thấy", "Not found"));
  if (!lesson) return <NotFoundPage />;

  const idx = LESSONS.indexOf(lesson);
  const prev = LESSONS[idx - 1];
  const next = LESSONS[idx + 1];
  const stage = STAGES.find((s) => s.id === lesson.stage);
  const done = !!p.lessons[lesson.id];

  return (
    <article className="lesson-body" style={{ maxWidth: 780, margin: "0 auto" }}>
      <p className="small"><Link to="/learn"><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Lộ trình học", "Learning path")}</Link> · {t("Chặng", "Stage")} {lesson.stage}: {stage?.title}</p>
      <h1>{lesson.title}</h1>
      <div className="row small muted">
        <span><Clock className="ico" size={16} aria-hidden="true" /> {lesson.minutes} {t("phút", "min")}</span>
        {done && <span className="chip ok"><Check className="ico" size={14} aria-hidden="true" /> {t("Đã hoàn thành", "Completed")}</span>}
      </div>

      <div className="callout mt">
        <strong>{t("Sau bài này bạn sẽ:", "After this lesson you will:")}</strong>
        <ul className="mb-0">{lesson.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
      </div>

      {lesson.sections.map((s) => (
        <section key={s.heading}>
          <h2>{s.heading}</h2>
          <RichText text={s.body} />
        </section>
      ))}

      {lesson.quizIds.length > 0 && (
        <section>
          <h2><ListChecks className="ico" size={22} aria-hidden="true" />{t("Kiểm tra nhanh", "Quick check")}</h2>
          <RequireAuth><QuickCheck key={lesson.id} ids={lesson.quizIds} /></RequireAuth>
        </section>
      )}

      {lesson.labIds.length > 0 && (
        <section>
          <h2><FlaskConical className="ico" size={22} aria-hidden="true" />{t("Thực hành", "Hands-on")}</h2>
          <div className="grid grid-2">
            {lesson.labIds.map((id) => {
              const raw = LAB_BY_ID.get(id);
              if (!raw) return null;
              const lab = localizeLab(raw, p.lang);
              const best = p.labs[id]?.best;
              return (
                <Link key={id} to={`/labs/${id}`} className="card link">
                  <div style={{ fontWeight: 600 }}>{lab.title}</div>
                  <div className="small muted">{lab.skill} · {lab.minutes} {t("phút", "min")}</div>
                  {best !== undefined && <span className={`chip mt ${best === 100 ? "ok" : "warn"}`}>{t("Tốt nhất", "Best")}: {best}/100</span>}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <div className="card row between mt-lg">
        {prev ? <Link className="btn" to={`/learn/${prev.id}`}><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Bài trước", "Previous")}</Link> : <span />}
        {!done ? (
          <button type="button" className="btn primary" onClick={() => progressStore.update((x) => completeLesson(x, lesson.id, Date.now()))}>
            {t("Đánh dấu hoàn thành (+20 XP)", "Mark as complete (+20 XP)")}
          </button>
        ) : next ? (
          <Link className="btn primary" to={`/learn/${next.id}`}>{t("Bài tiếp", "Next")}: {next.title} <ArrowRight className="ico" size={16} aria-hidden="true" /></Link>
        ) : (
          <Link className="btn primary" to="/exams">{t("Thi thử ngay", "Take a mock exam")} <ArrowRight className="ico" size={16} aria-hidden="true" /></Link>
        )}
      </div>
    </article>
  );
}
